import React, { useCallback, useMemo, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { ff } from '@atlassian/jira-feature-flagging';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import type { tableBody_nativeIssueTable$key as IssueResultsFragment } from '@atlassian/jira-relay/src/__generated__/tableBody_nativeIssueTable.graphql';

import type { Column, TableInjectedCellProps } from '../../../common/types';
// eslint-disable-next-line jira/import/no-parent-imports
import Row from '../../row';

export type Props = {
	issueResults: IssueResultsFragment;
	columns: Column[];
	tableInjectedProps: TableInjectedCellProps;
	hasInlineEditing: boolean;
	hasFixedTableLayout: boolean;
	enableRowDragging: boolean;
};

const TableBody = ({
	issueResults,
	columns,
	tableInjectedProps,
	hasInlineEditing,
	hasFixedTableLayout,
	enableRowDragging,
}: Props) => {
	const issueResultsData = useFragment<IssueResultsFragment>(
		graphql`
			fragment tableBody_nativeIssueTable on JiraIssueConnection
			@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
				__id
				edges {
					node {
						key
						...row_nativeIssueTable @arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
					}
				}
			}
		`,
		issueResults,
	);

	const renderRow = useCallback(
		(index: number) => {
			const issueRow = issueResultsData?.edges?.[index];
			return (
				<ContextualAnalyticsData
					key={issueRow?.node?.key || index}
					attributes={{ rowIndex: index, tableRowNumber: index }}
				>
					<Row
						rowIndex={index}
						fragment={issueRow?.node ?? null}
						columns={columns}
						tableInjectedProps={tableInjectedProps}
						hasInlineEditing={hasInlineEditing}
						hasFixedTableLayout={hasFixedTableLayout}
						enableRowDragging={enableRowDragging}
						issuesConnectionId={issueResultsData.__id}
					/>
				</ContextualAnalyticsData>
			);
		},
		[
			columns,
			enableRowDragging,
			hasInlineEditing,
			hasFixedTableLayout,
			issueResultsData.__id,
			issueResultsData?.edges,
			tableInjectedProps,
		],
	);

	const rows = useMemo<ReactNode[]>(
		() => issueResultsData?.edges?.map((_, index: number) => renderRow(index)) ?? [],
		[issueResultsData?.edges, renderRow],
	);

	return <StyledTableBody>{rows}</StyledTableBody>;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledTableBody = styled.tbody(
	{
		borderBottom: 'none',
	},
	// Ensure position is correctly calculated when an inline edit popup is opened that overflows the table body
	() => ff('nin-resizable-columns_8z4eo') && { overflowX: 'hidden' },
);

export default TableBody;
